import * as React from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { RotateCcw } from "lucide-react"

import { DataState } from "@/components/DataState"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useToast } from "@/components/ui/use-toast"
import { useAuth } from "@/lib/auth"
import { t } from "@/lib/i18n"
import { nombreCuenta } from "@/routes/finance/mapping"
import { cn } from "@/lib/utils"
import { reportWriteError } from "@/lib/writeErrors"

import {
  borrarSupuesto,
  fetchSupuestos,
  guardarSupuesto,
  type IdEscenario,
  type SupuestosDeEscenario,
  type TipoDeSupuesto,
} from "@/routes/forecast/api"

const ESCENARIOS: IdEscenario[] = ["pesimista", "moderado", "optimista"]

/**
 * LOS SUPUESTOS DEL ESCENARIO, editables.
 *
 * Punto 6 de Cesar, primera mitad: «editar los supuestos por escenario».
 *
 * ---------------------------------------------------------------------------
 * LOS TRES A LA VEZ, EN COLUMNAS
 *
 * Por lo mismo que la tabla de escenarios: el valor de tener tres está en la
 * DISTANCIA entre ellos. Un supuesto se decide comparando —«si en el malo la
 * planilla no se mueve y en el bueno tampoco, ¿para qué está la columna?»— y
 * una distancia no se lee en secuencia.
 *
 * ---------------------------------------------------------------------------
 * LO QUE NO SE TOCA SE VE QUE NO SE TOCÓ
 *
 * Cada campo enseña su valor por defecto debajo, y los tocados llevan marca y
 * un botón de deshacer. Es la diferencia entre un escenario discutible y uno
 * ya decidido, y sin la marca no se distinguen: los dos son un número en una
 * caja.
 *
 * Y el mensaje del pie dice la consecuencia de tocar: lo que no se toca recibe
 * las mejoras de Marty; lo que se toca se queda como se dejó.
 */
export function SupuestosDialog({
  anio, open, onOpenChange,
}: {
  anio: number
  open: boolean
  onOpenChange: (v: boolean) => void
}) {
  const { activeTenantId } = useAuth()
  const q = useQuery({
    queryKey: ["forecast-supuestos", activeTenantId, anio],
    queryFn: () => fetchSupuestos(anio),
    enabled: Boolean(activeTenantId) && open,
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>{t("Supuestos del forecast")} · {anio}</DialogTitle>
          <DialogDescription>
            {t("Marty no adivina mejor que tú lo que va a pasar: lo que hace es poner los bordes. Estos son los supuestos con los que los calcula.")}
          </DialogDescription>
        </DialogHeader>

        <DataState
          loading={q.isLoading}
          error={q.error}
          empty={!q.isLoading && !(q.data?.escenarios ?? []).length}
          onRetry={() => void q.refetch()}
          rows={5}
        >
          <div className="max-h-[60vh] space-y-4 overflow-y-auto pr-1">
            <Bloque
              titulo={t("Cuánto se mueve el ingreso")}
              ayuda={t("Respecto de tu propia proyección. El moderado es tu hoja intacta, por eso vale 1.")}
              anio={anio}
              escenarios={q.data?.escenarios ?? []}
              tipo="factor_ingreso"
              leer={(e) => e.factor_ingreso}
              porDefecto={(e) => e.factor_por_defecto}
              paso={0.05}
              min={0.01}
              max={5}
            />

            <BloqueElasticidades
              anio={anio}
              escenarios={q.data?.escenarios ?? []}
            />

            <Bloque
              titulo={t("Cuántos días tarda en cobrarse")}
              ayuda={t("Después del vencimiento. El escenario malo de cobranza no es «cobro menos»: es «cobro más tarde», y el hueco de caja es el problema de verdad.")}
              anio={anio}
              escenarios={q.data?.escenarios ?? []}
              tipo="retraso_dias"
              leer={(e) => e.retraso_dias}
              porDefecto={(e) => e.retraso_por_defecto}
              paso={1}
              min={0}
              max={365}
              sufijo={t("días")}
            />

            <Bloque
              titulo={t("Qué parte de la cartera no se cobra")}
              ayuda={t("Lo que de verdad se pierde, y es mucho menos de lo que se retrasa. En tanto por uno: 0,08 son ocho puntos.")}
              anio={anio}
              escenarios={q.data?.escenarios ?? []}
              tipo="merma_pct"
              leer={(e) => e.merma_pct}
              porDefecto={(e) => e.merma_por_defecto}
              paso={0.01}
              min={0}
              max={1}
            />
          </div>
        </DataState>

        {q.data?.nota ? (
          <p className="marty-nota text-2xs leading-4">{q.data.nota}</p>
        ) : null}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("Cerrar")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** Una fila de supuesto, con una columna por escenario. */
function Bloque({
  titulo, ayuda, anio, escenarios, tipo, leer, porDefecto, paso, min, max, sufijo,
}: {
  titulo: string
  ayuda: string
  anio: number
  escenarios: SupuestosDeEscenario[]
  tipo: TipoDeSupuesto
  leer: (e: SupuestosDeEscenario) => number
  porDefecto: (e: SupuestosDeEscenario) => number
  paso: number
  min: number
  max: number
  sufijo?: string
}) {
  return (
    <section className="rounded-md border">
      <header className="border-b bg-surface-sunken/60 px-2.5 py-1.5">
        <h3 className="text-xs font-medium">{titulo}</h3>
        <p className="text-2xs leading-4 text-muted-foreground">{ayuda}</p>
      </header>
      <div className="grid gap-2 p-2.5 sm:grid-cols-3">
        {ESCENARIOS.map((id) => {
          const e = escenarios.find((x) => x.escenario === id)
          if (!e) return <div key={id} />
          return (
            <CampoDeSupuesto
              key={id}
              anio={anio}
              escenario={id}
              etiqueta={e.nombre}
              tipo={tipo}
              valor={leer(e)}
              porDefecto={porDefecto(e)}
              editado={e.editados.includes(tipo)}
              paso={paso}
              min={min}
              max={max}
              sufijo={sufijo}
            />
          )
        })}
      </div>
    </section>
  )
}

/**
 * LAS ELASTICIDADES, que son el supuesto que más cambia el resultado.
 *
 * Una cuenta fija no se mueve aunque el ingreso caiga un 20%. Un escenario
 * donde la planilla baja con las ventas nunca da miedo, y por eso no sirve —
 * pero un cliente que de verdad puede recortar tiene que poder declararlo.
 */
function BloqueElasticidades({
  anio, escenarios,
}: {
  anio: number
  escenarios: SupuestosDeEscenario[]
}) {
  const cuentas = escenarios[0]?.elasticidades ?? []
  return (
    <section className="rounded-md border">
      <header className="border-b bg-surface-sunken/60 px-2.5 py-1.5">
        <h3 className="text-xs font-medium">
          {t("Qué cuentas siguen al ingreso")}
        </h3>
        <p className="text-2xs leading-4 text-muted-foreground">
          {t("0 = fija (no se mueve con las ventas) · 1 = totalmente variable. Lo que no sepas, déjalo: por defecto una cuenta desconocida es FIJA, y eso hace que el escenario malo duela lo que tiene que doler.")}
        </p>
      </header>
      <div className="divide-y">
        {cuentas.map((c) => (
          <div key={c.cuenta} className="grid gap-2 p-2.5 sm:grid-cols-[10rem_1fr]">
            <div className="self-center text-xs">{nombreCuenta(c.cuenta)}</div>
            <div className="grid gap-2 sm:grid-cols-3">
              {ESCENARIOS.map((id) => {
                const e = escenarios.find((x) => x.escenario === id)
                const actual = e?.elasticidades.find((x) => x.cuenta === c.cuenta)
                if (!e || !actual) return <div key={id} />
                return (
                  <CampoDeSupuesto
                    key={id}
                    anio={anio}
                    escenario={id}
                    etiqueta={e.nombre}
                    tipo="elasticidad"
                    cuenta={c.cuenta}
                    valor={actual.valor}
                    porDefecto={actual.por_defecto}
                    editado={actual.editado}
                    paso={0.1}
                    min={0}
                    max={1}
                  />
                )
              })}
            </div>
          </div>
        ))}
      </div>
    </section>
  )
}

function CampoDeSupuesto({
  anio, escenario, etiqueta, tipo, cuenta, valor, porDefecto, editado,
  paso, min, max, sufijo,
}: {
  anio: number
  escenario: IdEscenario
  etiqueta: string
  tipo: TipoDeSupuesto
  cuenta?: string
  valor: number
  porDefecto: number
  editado: boolean
  paso: number
  min: number
  max: number
  sufijo?: string
}) {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [texto, setTexto] = React.useState(String(valor))

  React.useEffect(() => setTexto(String(valor)), [valor])

  function invalidar() {
    for (const k of ["forecast-supuestos", "forecast-proyeccion", "forecast-caja",
                     "forecast-cobranza"]) {
      void queryClient.invalidateQueries({ queryKey: [k] })
    }
  }

  const guardar = useMutation({
    mutationFn: (v: number) =>
      guardarSupuesto(anio, { escenario, tipo, cuenta: cuenta ?? null, valor: v }),
    onSuccess: () => {
      invalidar()
      toast({ title: t("Supuesto guardado"),
              description: t("La proyección de este escenario ya lo usa.") })
    },
    onError: (e) => {
      // SE VUELVE AL VALOR ANTERIOR. Dejar en la caja un número que el
      // servidor rechazó haría creer que está guardado.
      setTexto(String(valor))
      reportWriteError(e, { entidad: "el supuesto", operacion: "guardar" })
    },
  })

  const deshacer = useMutation({
    mutationFn: () => borrarSupuesto(anio, { escenario, tipo, cuenta: cuenta ?? null }),
    onSuccess: () => {
      invalidar()
      toast({ title: t("Vuelve al valor de Marty") })
    },
    onError: (e) => reportWriteError(e, { entidad: "el supuesto", operacion: "deshacer" }),
  })

  function confirmar() {
    const v = Number(texto.replace(",", "."))
    if (!Number.isFinite(v) || v < min || v > max) {
      setTexto(String(valor))
      toast({
        title: t("Fuera de rango"),
        // UNA SOLA FRASE Y NO TRES PEGADAS. «Tiene que estar entre» + min +
        // «y» + max se traduce mal: en portugués y en inglés el orden de esas
        // piezas no es el mismo, y una conjunción suelta en el diccionario no
        // tiene a qué traducirse. La frase entera con marcadores sí.
        description: t("Tiene que estar entre {min} y {max}.")
          .replace("{min}", String(min)).replace("{max}", String(max)),
        variant: "destructive",
      })
      return
    }
    if (v === valor) return
    guardar.mutate(v)
  }

  const id = `sup-${escenario}-${tipo}-${cuenta ?? ""}`
  return (
    <div className="space-y-0.5">
      <Label htmlFor={id} className="flex items-center gap-1 text-2xs">
        {etiqueta}
        {editado ? (
          <Badge variant="outline" className="h-4 px-1 text-[10px]">
            {t("tuyo")}
          </Badge>
        ) : null}
      </Label>
      <div className="flex items-center gap-1">
        <Input
          id={id}
          type="number"
          step={paso}
          min={min}
          max={max}
          value={texto}
          onChange={(ev) => setTexto(ev.target.value)}
          onBlur={confirmar}
          onKeyDown={(ev) => { if (ev.key === "Enter") ev.currentTarget.blur() }}
          className={cn("h-7 text-xs", editado && "border-brand")}
          disabled={guardar.isPending || deshacer.isPending}
        />
        {editado ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7 shrink-0"
            title={`${t("Volver al valor de Marty")}: ${porDefecto}`}
            onClick={() => deshacer.mutate()}
            disabled={deshacer.isPending}
          >
            <RotateCcw className="h-3 w-3" aria-hidden="true" />
            <span className="sr-only">{t("Volver al valor de Marty")}</span>
          </Button>
        ) : null}
      </div>
      <p className="text-[10px] leading-3 text-muted-foreground">
        {editado ? `${t("Marty usaría")} ${porDefecto}` : t("valor de Marty")}
        {sufijo ? ` · ${sufijo}` : ""}
      </p>
    </div>
  )
}
